<template>
  <div class="pie-chart-wrap" :style="{ height, width }">
    <!-- 无数据时图表已销毁，用语义块复现与 ECharts title 一致的标题/副标题（仅 complex 与有图时一致） -->
    <div
      v-if="chartEmpty && emptyTitleBlockVisible"
      class="pie-chart-empty-title"
      :class="emptyTitlePositionClass"
    >
      <div v-if="emptyMeta.title" class="pie-chart-empty-title-text">{{ emptyMeta.title }}</div>
      <div v-if="emptyMeta.subtext" class="pie-chart-empty-title-sub">{{ emptyMeta.subtext }}</div>
    </div>
    <div
      v-show="!chartEmpty"
      :id="id"
      :class="[className, 'pie-chart-canvas']"
      :style="{ height: '100%', width: '100%' }"
    />
    <div v-if="chartEmpty" class="pie-chart-empty">
      <el-empty description="暂无数据" />
    </div>
  </div>
</template>

<script>
import echarts from 'echarts'
import moment from 'moment-timezone'
import resize from "@/components/Charts/mixins/resize";


export default {
  mixins: [resize],
  props: {
    className: {
      type: String,
      default: 'chart'
    },
    id: {
      type: String,
      default: 'chart'
    },
    width: {
      type: String,
      default: '100%'
    }, color: {
      type: String,
      default: null
    },
    height: {
      type: String,
      default: '100%'
    },
    path: {
      type: String
    },
    show_mode: {
      type: String,
      default: 'complex'
    },
    params: {}
  },
  data() {
    return {
      chart: null,
      /** 接口无数据或空数组时为 true，展示 el-empty */
      chartEmpty: false,
      /**
       * 无数据时仍展示的标题信息（来自接口 payload，与 setOption 中 title 字段同源）
       * 有数据或请求失败时为 null
       */
      emptyMeta: null,
    }
  },
  computed: {
    /**
     * 是否在空态下展示标题区：与图表 option 中 title.show === (show_mode === 'complex') 保持一致
     */
    emptyTitleBlockVisible() {
      return (
        this.show_mode === 'complex' &&
        this.emptyMeta &&
        (this.emptyMeta.title || this.emptyMeta.subtext)
      )
    },
    /**
     * 与接口 title_postition 对齐的横向对齐样式类
     */
    emptyTitlePositionClass() {
      const pos = (this.emptyMeta && this.emptyMeta.title_position) || 'left'
      if (pos === 'center') {
        return 'pie-empty-title--center'
      }
      if (pos === 'right') {
        return 'pie-empty-title--right'
      }
      return 'pie-empty-title--left'
    },
  },
  mounted() {
    this.initChart()
  },
  beforeDestroy() {
    if (!this.chart) {
      return
    }
    this.chart.dispose()
    this.chart = null
  },
  watch: {
    '$route': function (to, from) {
      if (to != from) {
        this.initChart();
      }
    }
  },
  methods: {
    /**
     * 计算开始时间：结束时间减去365天
     * @param {string} endDate 结束时间字符串
     * @returns {string} YYYY-MM-DD HH:mm:ss
     */
    calculateStartDate(endDate) {
      // 将结束时间字符串转换为 moment 对象
      const endMoment = moment(endDate, 'YYYY-MM-DD HH:mm:ss')
      // 减去365天，并设置为当天的开始时间（00:00:00）
      const startMoment = endMoment.clone().subtract(365, 'days').startOf('day')
      // 返回格式化后的开始时间字符串
      return startMoment.format('YYYY-MM-DD HH:mm:ss')
    },
    /**
     * 判断饼图业务数据是否为空（null、非数组、空数组）
     * @param {*} raw 接口 res.data.data
     * @returns {boolean}
     */
    isPieDataEmpty(raw) {
      if (raw == null) {
        return true
      }
      if (!Array.isArray(raw)) {
        return true
      }
      return raw.length === 0
    },
    initChart() {
      this.chartEmpty = false
      this.emptyMeta = null
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
      this.$nextTick(() => {
        const el = document.getElementById(this.id)
        if (!el) {
          return
        }
        this.chart = echarts.init(el, 'dark')
        this.chart.showLoading({
          text: '加载中...'
        })
        var post_params = Object.assign({}, {"extra_params": this.params});
        if(post_params.date_end){
          post_params.date_start = this.calculateStartDate(post_params.date_end)
        }

        this.$http.post(process.env.VUE_APP_API_HOST + "/" + this.path, post_params, {"content-type": "application/json"}).then((res) => {
          // 组件可能已销毁，确保图表实例仍存在
          if (!this.chart) {
            return
          }
          this.chart.hideLoading()
          const payload = res.data || {}
          const rawData = payload.data
          if (this.isPieDataEmpty(rawData)) {
            this.emptyMeta = {
              title: payload.title ? payload.title : '',
              subtext: payload.subtext ? payload.subtext : '',
              title_position: payload.title_postition ? payload.title_postition : 'left',
            }
            this.chartEmpty = true
            this.chart.dispose()
            this.chart = null
            return
          }
          const dataList = rawData.slice().sort(function (a, b) {
            return a.value - b.value;
          })
          this.chart.setOption({
            animationDuration:3000,
            animationDurationUpdate:3000,
            color: ["#5470c6", "#91cc75", "#fac858", "#ee6666", "#73c0de", "#3ba272", "#fc8452", "#9a60b4", "#ea7ccc"],
            backgroundColor:  this.show_mode === "complex" ? "#080A20" : "#42444F",
            tooltip: {
              trigger: 'item',
              formatter: function (params) {
                var data = params.data;
                var val = parseInt(data.value);
                return data.name + ':' + Math.round(val).toLocaleString() + '(' + params.percent + '%)'
              }
            },
            toolbox: {
              show: this.show_mode === "complex" ? true : false,
              feature: {
                saveAsImage: {show: true},
                myTool1: {
                  show: true,
                  title: '全屏',
                  icon: "path://M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z",
                  onclick: () => {
                    const element = document.getElementById(this.id);
                    if (element.requestFullScreen) { // HTML W3C 提议
                      element.requestFullScreen();
                    } else if (element.msRequestFullscreen) { // IE11
                      element.msRequestFullScreen();
                    } else if (element.webkitRequestFullScreen) { // Webkit (works in Safari5.1 and Chrome 15)
                      element.webkitRequestFullScreen();
                    } else if (element.mozRequestFullScreen) { // Firefox (works in nightly)
                      element.mozRequestFullScreen();
                    }
                    // 退出全屏
                    if (element.requestFullScreen) {
                      document.exitFullscreen();
                    } else if (element.msRequestFullScreen) {
                      document.msExitFullscreen();
                    } else if (element.webkitRequestFullScreen) {
                      document.webkitCancelFullScreen();
                    } else if (element.mozRequestFullScreen) {
                      document.mozCancelFullScreen();
                    }
                  },
                }
              }
            },
            label: {
              alignTo: 'edge',
              edgeDistance: 10,
              normal: {
                overflow: 'none',
                textStyle: {
                  fontSize: 14
                },
              }
            },
            labelLine: {
              length: 15,
              length2: 0,
              maxSurfaceAngle: 80
            },
            title: {
              text: payload.title ? payload.title : "",
              subtext: payload.subtext ? payload.subtext : "",
              left: payload.title_postition ? payload.title_postition : 'left',
              show: this.show_mode === "complex" ? true : false,
              fontSize: 14
            },
            // legend: {
            //   top: '5%',
            //   left: 'center'
            // },
            series: [
              {
                name: payload.name,
                type: 'pie',
                data: dataList,
                radius: ['40%', '60%'],
                center: ['50%', '50%'],
                // roseType: 'area',
                "label": {
                  alignTo: 'edge',
                  edgeDistance: 10,
                  "normal": {
                    "show": true,
                    "textStyle": {
                      "fontSize": 14
                    }
                  },
                  "emphasis": {
                    "show": this.show_mode === "complex" ? true : false,
                  }
                },
                itemStyle: {
                  borderRadius: 8
                }
              }
            ]
          })
        }).catch(() => {
          if (!this.chart) {
            return
          }
          this.chart.hideLoading()
          this.chart.dispose()
          this.chart = null
          this.emptyMeta = null
          this.chartEmpty = true
        })
      })
    }
  }
}
</script>

<style scoped>
.pie-chart-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 120px;
}

.pie-chart-canvas {
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
}

.pie-chart-empty-title {
  box-sizing: border-box;
  padding: 8px 12px 0;
  flex-shrink: 0;
}

.pie-empty-title--left {
  text-align: left;
}

.pie-empty-title--center {
  text-align: center;
}

.pie-empty-title--right {
  text-align: right;
}

.pie-chart-empty-title-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.4;
}

.pie-chart-empty-title-sub {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.35;
}

.pie-chart-empty {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: 0;
  max-height: 100%;
  box-sizing: border-box;
  padding: 16px 12px;
  overflow: hidden;
}

/* 空状态整块不超出父级；插图区域按比例缩小，留出文案空间 */
.pie-chart-empty :deep(.el-empty) {
  height: 100%;
  max-height: 100%;
  box-sizing: border-box;
  padding: 0 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.pie-chart-empty :deep(.el-empty__description) {
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
  margin-top: 8px;
}

.pie-chart-empty :deep(.el-empty__image) {
  opacity: 0.85;
  flex: 0 1 auto;
  min-height: 0;
  max-height: 65%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pie-chart-empty :deep(.el-empty__image img),
.pie-chart-empty :deep(.el-empty__image svg) {
  max-height: 100%;
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  vertical-align: middle;
}
</style>
